import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { FaEye, FaEyeSlash, FaLock, FaUser } from 'react-icons/fa';
import axios from 'axios';
import API_BASE_URL from '../../../js/urlHelper';
import jwtUtils from '../../../utilities/jwtUtils';

const ROLE_ROUTES = {
  admin: '/admin/home',
  cliente: '/cliente/estados',
  asesor: '/asesor/home',
  auditor: '/auditor/home',
  cajero: '/cajero/home',
  operaciones: '/operaciones/calculadora',
  contabilidad: '/contabilidad/finanzas',
};
const logo = `${process.env.PUBLIC_URL}/Logo_FICSULLANA2.webp`;

const LoginForm = ({ onLoginSuccess }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [dni, setDni] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const timeoutRef = useRef(null);
  const dniInputRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!showForgotPassword) {
      return;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setShowForgotPassword(false);
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);

    if (dniInputRef.current) {
      dniInputRef.current.focus();
    }

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showForgotPassword]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/login`,
        {
          username,
          password,
          remember_me: rememberMe,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      const result = response.data;

      const access_token = result.access_token;
      const refresh_token = result.refresh_token;
      const idRefreshToken = result.idRefreshToken;
      const secureCookieFlag = window.location.protocol === 'https:' ? '; Secure' : '';

      // Configure cookie options based on "Remember Me"
      const accessTokenExpiration = `; path=/; SameSite=Strict${secureCookieFlag}`;
      const refreshTokenExpiration = rememberMe
        ? `; expires=${new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toUTCString()}; path=/; SameSite=Strict${secureCookieFlag}`
        : `; path=/; SameSite=Strict${secureCookieFlag}`;
      const refreshTokenIDExpiration = rememberMe
        ? `; expires=${new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toUTCString()}; path=/; SameSite=Strict${secureCookieFlag}`
        : `; path=/; SameSite=Strict${secureCookieFlag}`;

      // Set cookies
      document.cookie = `access_token=${access_token}${accessTokenExpiration}`;
      document.cookie = `refresh_token=${refresh_token}${refreshTokenExpiration}`;
      document.cookie = `refresh_token_id=${idRefreshToken}${refreshTokenIDExpiration}`;

      const rol = jwtUtils.getUserRole(access_token);
      const route = ROLE_ROUTES[rol];

      if (route) {
        toast.success('Inicio de sesión exitoso');
        if (onLoginSuccess) {
          onLoginSuccess(true);
        }

        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
          window.location.href = route;
        }, 2000);
      } else {
        console.error('Rol no reconocido:', rol);
        toast.error(`Rol no reconocido: ${rol}`);
      }
    } catch (error) {
      if (error.response) {
        const { message, schedule_restricted } = error.response.data;

        if (error.response.status === 403 && schedule_restricted) {
          toast.error(message, {
            autoClose: 8000,
          });
        } else if (error.response.status === 403 && message.includes('cambiar tu contraseña')) {
          toast.info(message, { autoClose: 5000 });
        }
        // Otros errores
        else {
          toast.error(message || 'Usuario o contraseña incorrectos');
        }
      } else {
        console.error('Error al intentar iniciar sesión:', error);
        toast.error('Error interno del servidor. Por favor, inténtelo de nuevo más tarde.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotPasswordLoading(true);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/forgot-password`,
        { dni },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      toast.success(response.data.message || 'Se ha enviado un enlace de restablecimiento a tu correo.');
      setShowForgotPassword(false);
      setDni('');
    } catch (error) {
      if (error.response) {
        toast.error(error.response.data.message || 'DNI no encontrado o error al enviar el correo.');
      } else {
        console.error('Error al solicitar restablecimiento:', error);
        toast.error('Error interno del servidor. Por favor, inténtelo de nuevo más tarde.');
      }
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  return (
    <div className="space-y-6 text-left">
      <img
        src={logo}
        alt="Logo FIC Sullana"
        className="mb-6 mx-auto block w-60 sm:w-68"
        loading="eager"
        fetchPriority="high"
      />

      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.35em] text-red-500">Bienvenido</p>
        <h1 className="mt-3 text-3xl font-semibold text-slate-900">Iniciar sesión</h1>
      </div>

      <form className="space-y-5" onSubmit={handleLogin}>
        <div>
          <label htmlFor="username" className="block text-sm font-medium text-slate-700">
            Usuario
          </label>
          <div className="mt-2 flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-4 py-2.5 shadow-sm focus-within:border-red-400 focus-within:ring-2 focus-within:ring-red-200">
            <FaUser className="text-red-400" size={16} aria-hidden="true" />
            <input
              id="username"
              type="text"
              placeholder="Tu usuario"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full bg-transparent text-sm text-slate-900 placeholder-slate-400 focus:outline-none"
              autoComplete="username"
              required
            />
          </div>
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-slate-700">
            Contraseña
          </label>
          <div className="relative mt-2 flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-4 py-2.5 shadow-sm focus-within:border-red-400 focus-within:ring-2 focus-within:ring-red-200">
            <FaLock className="text-red-400" size={16} aria-hidden="true" />
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Tu contraseña"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-transparent pr-10 text-sm text-slate-900 placeholder-slate-400 focus:outline-none"
              autoComplete="current-password"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full border border-transparent p-2 text-slate-500 transition hover:text-slate-700 focus:border-red-200 focus:outline-none focus:ring-2 focus:ring-red-200"
              aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
              title={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            >
              {showPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
          <label className="flex items-center gap-2 text-slate-600">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-red-600 focus:ring-red-300"
            />
            Recordarme por 7 días
          </label>
          <button
            type="button"
            className="font-medium text-red-500 transition hover:text-red-600"
            onClick={() => setShowForgotPassword(true)}
          >
            ¿Olvidaste tu contraseña?
          </button>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full rounded-full bg-red-500 px-4 py-2.5 text-sm font-semibold text-white shadow-[0_12px_30px_-18px_rgba(239,68,68,0.8)] transition hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-300 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {loading ? 'Procesando...' : 'Iniciar sesión'}
        </button>
      </form>

      {showForgotPassword && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 px-4">
          <div
            className="w-full max-w-md rounded-3xl bg-white p-8 shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="forgot-password-title"
            aria-describedby="forgot-password-description"
          >
            <h2 id="forgot-password-title" className="text-2xl font-semibold text-slate-900">
              Restablecer contraseña
            </h2>
            <p id="forgot-password-description" className="mt-2 text-sm text-slate-500">
              Ingresa tu DNI para enviarte un enlace de restablecimiento.
            </p>
            <form className="mt-6 space-y-4" onSubmit={handleForgotPassword}>
              <div>
                <label htmlFor="dni" className="block text-sm font-medium text-slate-700">
                  DNI
                </label>
                <input
                  ref={dniInputRef}
                  type="text"
                  id="dni"
                  value={dni}
                  onChange={(e) => setDni(e.target.value)}
                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm transition focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-200"
                  placeholder="Ingresa tu DNI"
                  required
                />
              </div>
              <div className="flex items-center justify-between gap-3">
                <button
                  type="button"
                  className="text-sm font-medium text-slate-500 transition hover:text-slate-700"
                  onClick={() => setShowForgotPassword(false)}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={forgotPasswordLoading}
                  className="rounded-2xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-md transition hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {forgotPasswordLoading ? 'Enviando...' : 'Enviar correo'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginForm;
